import { useState, useEffect, useMemo, Suspense, useRef } from 'react';
import { Check, Loader2, Plus } from 'lucide-react';
import { motion } from 'motion/react';
import { Link, useLocation } from 'react-router-dom';
import { SidebarProvider } from '@primitive/sidebar';
import { Button } from '@primitive/button';
import { PageLoader } from '@complex/PageLoader';
import {
    type BlockBadgeProgressSnapshot,
    useCelebration,
} from '@complex/CelebrationProvider';
import { useDashboardMotion } from '@primitive/motion';
import { useIsMobile } from '@primitive/use-mobile';
import { cn } from '@utils';

import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '@primitive/alert-dialog';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@primitive/dialog';
import { Badge } from '@primitive/badge';
import { CommunityRecord, PublicUserProfileView, Notification, CommunityViewPublic, CommunityViewPrivate, CampaignPublicView } from "@bbb/model";

function mergeOwnedCommunitiesByUpdatedAt(
    previous: CommunityViewPrivate[] | null,
    incoming: CommunityViewPrivate[] | null,
): CommunityViewPrivate[] | null {
    // If the network data is missing (still loading or errored), keep whatever we already have.
    if (!incoming && previous) return previous;
    if (!incoming) return null;

    const mergedMap = new Map(
        (previous ?? []).map((community) => [community.id, community]),
    );

    const isNewer = (next?: CommunityViewPrivate, prev?: CommunityViewPrivate) => {
        if (!prev || !next) return Boolean(next);
        const nextTime = next.updatedAt ? new Date(next.updatedAt).getTime() : 0;
        const prevTime = prev.updatedAt ? new Date(prev.updatedAt).getTime() : 0;
        return nextTime > prevTime;
    };

    incoming.forEach((community) => {
        const prev = mergedMap.get(community.id);
        mergedMap.set(community.id, isNewer(community, prev) ? community : prev ?? community);
    });

    // Preserve locally created communities that haven't been returned by the API yet.
    return Array.from(mergedMap.values());
}

function areCommunitiesEqual(
    a: CommunityViewPrivate[] | null,
    b: CommunityViewPrivate[] | null,
) {
    if (a === b) return true;
    if (!a || !b) return false;
    if (a.length !== b.length) return false;
    for (let i = 0; i < a.length; i++) {
        const left = a[i];
        const right = b[i];
        if (left.id !== right.id) return false;
        if (left.updatedAt !== right.updatedAt) return false;
    }
    return true;
}

function toCommunityViewPublic(
    community: CommunityViewPrivate,
): CommunityViewPublic {
    return {
        id: community.id,
        profile: community.profile,
        teamMembers: community.teamMembers,
        createdAt: community.createdAt,
        updatedAt: community.updatedAt,
        participantsCount: community.participantsCount,
        followersCount: community.followersCount,
        totalRewards: community.totalRewards,
    };
}

function buildKnownCurrentBlockBadgeProgress(
    currentBlocks: number | null | undefined,
    rewardBlocks = 1,
): BlockBadgeProgressSnapshot {
    const safeReward = Math.max(1, Math.floor(Number(rewardBlocks) || 1));
    const safeCurrent = Math.max(
        safeReward,
        Math.floor(Number(currentBlocks) || safeReward),
    );

    return {
        previousBlocks: Math.max(0, safeCurrent - safeReward),
        currentBlocks: safeCurrent,
        rewardBlocks: safeReward,
    };
}
import { useAuthStore } from '@stores/useAuthStore';
import { useUIStore } from '@stores/useUIStore';
import { useCommunityStore } from '@stores/useCommunityStore';
import { useNotificationStore } from '@stores/useNotificationStore';
import { useShallow } from 'zustand/react/shallow';
import { achievementCelebrationStorage, campaignProgressStorage, favoritesStorage } from '@lib/localStorage';
import { TabFilter, SelectableUserType } from '@types';
import { LeaderboardUser } from "@interfaces";
import { FeedbackButton } from '@complex/FeedbackButton';
import { NotificationDetailsDialog } from '@complex/NotificationDetailsDialog';
import { Toaster } from '@primitive/sonner';
import { toast } from 'sonner';
import { UserTypeDialog } from '@complex/UserTypeDialog';
import { CookieDisclosure } from '@complex/CookieDisclosure';
import {
    usePrivateCommunitiesQuery,
    usePublicCommunitiesQuery,
} from '@lib/communityQueries';
import {
    useCampaignsForCommunities,
    useAllLiveCampaigns,
} from './stores/useCampaignStore';
import { hasEndedCampaigns as hasEndedCampaignsHelper, hasLiveCampaigns as hasLiveCampaignsHelper, matchesCampaignSearchQuery } from '@lib/campaignHelpers';
import { getLatestCampaignStatus, nowEpochMs } from "@bbb/utils";
import { useXCallbackEffect } from "@hooks/xCallback";
import { useDiscordCallbackEffect } from "@hooks/discordCallback";
import { useKYCCallbackEffect } from "@hooks/handleKYC";
import { CommunityUserDashboard, LoginDialog, SignupDialog } from './lazyLoader';
import { useBlocksUpdateHandler } from '@hooks/useBlocksUpdateHandler';
import { useHandleInviteToken } from '@hooks/useHandleInviteToken';
import { useHandleReferralToken } from '@hooks/useHandleReferralToken';
import { useNotifications, useMarkNotificationAsReadMutation, useMarkAllNotificationsAsReadMutation, useDeleteNotificationMutation, useDeleteAllNotificationsMutation, useCreateNotificationMutation } from '@hooks/useNotifications';
import { buildCampaignCommunityIndex, resolveNotificationNavigation } from '@lib/notificationNavigation';
import { buildNotificationPopupSummary } from '@lib/notificationPresentation';
import { playSound } from '@lib/sound';
import { buildPathForView, useDashboardRouting } from '@lib/routing';
import {
    isManagedCommunityRouteReady,
    resolveManagedCommunitySelection,
} from '@lib/communityAdminSelection';
import { isGuestRestrictedCampaignTab } from '@lib/tabAccess';
import { useUserProfile } from '@hooks/useUserProfile';
import { queryClient } from '@lib/queryClient';
import { shouldPromptForInitialUserType } from '@lib/initialUserTypePrompt';
import verifiedBadge from 'figma:asset/1974910ce964a47c21d0aef47fa3c3526e693885.png';
import { protectedViews } from "@constants";

const AUTH_PROMPT_PARAM = "auth";
const AUTH_PROMPT_LOGIN = "login";

export function AppContent() {
    const dashboardMotion = useDashboardMotion();
    const { celebrate } = useCelebration();
    const isMobileLayout = useIsMobile();

    //#region STATES
    const {
        isAuthenticated,
        isAuthReady,
        updateUserProfile,
        InitKycSession,
    } = useAuthStore(useShallow(s => ({
        isAuthenticated: s.isAuthenticated,
        isAuthReady: s.isAuthReady,
        updateUserProfile: s.updateUserProfile,
        InitKycSession: s.InitKycSession,
    })));
    const location = useLocation();
    const { data: user, isLoading: isUserProfileLoading } = useUserProfile({
        enabled: isAuthenticated,
    });
    const [achievementsViewUser, setAchievementsViewUser] = useState<PublicUserProfileView | LeaderboardUser | null>(null);
    const [publicProfileUserId, setPublicProfileUserId] = useState<string | null>(null);
    const [favoritesTrigger, setFavoritesTrigger] = useState(0);
    const [campaignUpdateTrigger, setCampaignUpdateTrigger] = useState(0);
    const [showUserTypeDialog, setShowUserTypeDialog] = useState(false);
    const [userTypeDialogValue, setUserTypeDialogValue] = useState<SelectableUserType | null>(null);
    const [isSavingUserType, setIsSavingUserType] = useState(false);
    const [feedbackNotificationDialog, setFeedbackNotificationDialog] = useState<{
        title: string;
        feedback: string;
        type: 'feedback' | 'kyc';
    } | null>(null);
    const [notificationDetailsDialog, setNotificationDetailsDialog] = useState<{
        title: string;
        notifications: Notification[];
    } | null>(null);
    const [isRetryingKyc, setIsRetryingKyc] = useState(false);
    const mainScrollRef = useRef<HTMLDivElement | null>(null);
    const scrollPositionRef = useRef(0);
    const knownNotificationIdsRef = useRef<Set<string> | null>(null);
    const {
        selectedCommunity,
        setSelectedCommunity,
        selectedCampaign,
        setSelectedCampaign,
        userOwnedCommunities,
        setUserOwnedCommunities,
        selectedPrivateCommunity,
        setSelectedPrivateCommunity,
        upsertPrivateCommunity,
        communities,
        setCommunities,
    } = useCommunityStore(useShallow((s) => ({
        selectedCommunity: s.selectedCommunity,
        setSelectedCommunity: s.setSelectedCommunity,
        selectedCampaign: s.selectedCampaign,
        setSelectedCampaign: s.setSelectedCampaign,
        userOwnedCommunities: s.userOwnedCommunities,
        setUserOwnedCommunities: s.setUserOwnedCommunities,
        selectedPrivateCommunity: s.selectedPrivateCommunity,
        setSelectedPrivateCommunity: s.setSelectedPrivateCommunity,
        upsertPrivateCommunity: s.upsertPrivateCommunity,
        communities: s.communities,
        setCommunities: s.setCommunities,
    })));

    const {
        activeTab,
        setActiveTab,
        selectedBadgeTier,
        setSelectedBadgeTier,
        searchQuery,
        viewMode,
        setViewMode,
        showLoginDialog,
        setShowLoginDialog,
        showSignupDialog,
        setShowSignupDialog,
        showFeedback,
        setShowFeedback,
        showCreateCommunityAuthDialog,
        setShowCreateCommunityAuthDialog,
        showCommunityPicker,
        setShowCommunityPicker,
    } = useUIStore(useShallow(s => ({
        activeTab: s.activeTab,
        setActiveTab: s.setActiveTab,
        selectedBadgeTier: s.selectedBadgeTier,
        setSelectedBadgeTier: s.setSelectedBadgeTier,
        searchQuery: s.searchQuery,
        setSearchQuery: s.setSearchQuery,
        viewMode: s.viewMode,
        setViewMode: s.setViewMode,
        showLoginDialog: s.modals.showLoginDialog,
        setShowLoginDialog: s.setShowLoginDialog,
        showSignupDialog: s.modals.showSignupDialog,
        setShowSignupDialog: s.setShowSignupDialog,
        showFeedback: s.modals.showFeedback,
        setShowFeedback: s.setShowFeedback,
        showCreateCommunityAuthDialog: s.modals.showCreateCommunityAuthDialog,
        setShowCreateCommunityAuthDialog: s.setShowCreateCommunityAuthDialog,
        showCommunityPicker: s.modals.showCommunityPicker,
        setShowCommunityPicker: s.setShowCommunityPicker,

    })));

    const {
        _notifications,
        setNotifications,
        markAsRead: markNotificationAsRead,
        markAllAsRead: markAllNotificationsAsRead,
        clearAll: clearNotifications,
    } = useNotificationStore(useShallow((s) => ({
        _notifications: s.notifications,
        setNotifications: s.setNotifications,
        markAsRead: s.markAsRead,
        markAllAsRead: s.markAllAsRead,
        clearAll: s.clearAll,
    })));

    const { data: apiNotifications } = useNotifications({ enabled: isAuthenticated });
    const markNotificationAsReadMutation = useMarkNotificationAsReadMutation();
    const markAllNotificationsAsReadMutation = useMarkAllNotificationsAsReadMutation();
    const deleteNotificationMutation = useDeleteNotificationMutation();
    const deleteAllNotificationsMutation = useDeleteAllNotificationsMutation();
    const createNotificationMutation = useCreateNotificationMutation();

    //#endregion
    //#region CUSTOM HOOKS
    /**
     * Handle X / Twitter OAuth callback
     */
    useXCallbackEffect();
    useDiscordCallbackEffect();

    /**
     * Handle KYC callback
     */
    useKYCCallbackEffect();

    useBlocksUpdateHandler();
    useHandleInviteToken({
        onAuthRequired: () => {
            setShowSignupDialog(true);
        },
    });
    useHandleReferralToken({
        onAuthRequired: () => {
            setShowSignupDialog(true);
        },
    });

    //#endregion

    useEffect(() => {
        if (!isAuthenticated) {
            knownNotificationIdsRef.current = null;
            clearNotifications();
            return;
        }

        if (apiNotifications) {
            const nextNotificationIds = new Set(apiNotifications.map((notification) => notification.id));
            const knownNotificationIds = knownNotificationIdsRef.current;
            const newNotifications = knownNotificationIds === null
                ? []
                : apiNotifications.filter((notification) => !knownNotificationIds.has(notification.id));
            const hasNewNotification =
                newNotifications.length > 0;

            knownNotificationIdsRef.current = nextNotificationIds;

            if (hasNewNotification) {
                playSound('notification');
                const summary = buildNotificationPopupSummary(newNotifications);
                toast(summary.title, {
                    description: summary.description,
                    action: {
                        label: 'View',
                        onClick: () => {
                            setNotificationDetailsDialog({
                                title: summary.title,
                                notifications: newNotifications,
                            });
                        },
                    },
                });
            }

            setNotifications(apiNotifications);
        }
    }, [apiNotifications, isAuthenticated, setNotifications, clearNotifications]);

    const displayUser = user;
    const initialUserType = useMemo(() => {
        if (!displayUser) return null;
        return displayUser.initialUserType ?? displayUser.userType ?? null;
    }, [displayUser?.id, displayUser?.userType, displayUser?.initialUserType]);
    const userType = displayUser?.userType ?? initialUserType;
    const userTypeResolved = userType !== null && userType !== undefined;
    const isCommunityUser = userType === 'community-user';
    const isCommunityAdminView = viewMode === 'community-admin';
    const isOwnerOrAdmin = useMemo(() => {
        return userType === 'community-owner' || userType === 'community-admin';
    }, [userType]);
    const usePrivateDiscoveryData = isOwnerOrAdmin && isCommunityAdminView;
    const shouldLoadPublicDiscovery = !isAuthenticated || !usePrivateDiscoveryData;
    const shouldLoadPrivateCommunities = isAuthenticated && userTypeResolved && !isCommunityUser;
    const { data: privateCommunitiesData, isLoading: loadingOwnedCommunities } = usePrivateCommunitiesQuery(shouldLoadPrivateCommunities);

    const {
        data: publicCommunitiesData,
        isError: publicCommunitiesError,
        error: publicCommunitiesErrorDetails,
        isLoading: isPublicCommunitiesLoading,
    } = usePublicCommunitiesQuery({ limit: 100 }, shouldLoadPublicDiscovery);
    const ownedCommunities = useMemo(
        () => userOwnedCommunities ?? privateCommunitiesData ?? [],
        [privateCommunitiesData, userOwnedCommunities],
    );
    const ownedCommunityIds = useMemo(
        () => ownedCommunities.map((community) => community.id).filter(Boolean),
        [ownedCommunities],
    );

    const publicCommunityIds = useMemo(
        () => (publicCommunitiesData ?? []).map((c) => c.id).filter(Boolean),
        [publicCommunitiesData],
    );

    const {
        data: allLiveCampaigns = [],
        isLoading: isLoadingAllLiveCampaigns,
        isFetching: isFetchingAllLiveCampaigns,
    } = useAllLiveCampaigns(shouldLoadPublicDiscovery, publicCommunityIds);

    const allowedUserTypes: SelectableUserType[] = useMemo(() => {
        if (!initialUserType) {
            // First-time signup: admin is invite-only, so only expose owner/user
            return ['community-owner', 'community-user'];
        }

        return initialUserType === 'community-admin'
            ? ['community-admin', 'community-user']
            : ['community-owner', 'community-user'];
    }, [initialUserType]);

    useEffect(() => {
        if (!displayUser) {
            setShowUserTypeDialog(false);
            return;
        }

        if (shouldPromptForInitialUserType({
            hasUserProfile: true,
            userType: displayUser.userType,
        })) {
            setUserTypeDialogValue(null);
            setShowUserTypeDialog(true);
            return;
        }

        if (!displayUser.userType) {
            setUserTypeDialogValue(null);
            setShowUserTypeDialog(false);
        }
    }, [
        displayUser?.id,
        displayUser?.userType,
    ]);

    const isProtectedView = protectedViews.includes(viewMode);
    const hasResolvedOwnedCommunities = !loadingOwnedCommunities;
    const isCommunityAdminDataReady = Boolean(
        userOwnedCommunities &&
        selectedPrivateCommunity &&
        isManagedCommunityRouteReady({
            isCommunityAdminView,
            pathname: location.pathname,
            selectedCommunityId: selectedPrivateCommunity.id,
        }),
    );
    const shouldShowLoading =
        !isAuthReady ||
        (isProtectedView && (!isAuthenticated || isUserProfileLoading || !displayUser)) ||
        // Prevent flashing the public dashboard while owner data is hydrating.
        (isCommunityAdminView && (!hasResolvedOwnedCommunities || !isCommunityAdminDataReady));

    useEffect(() => {
        if (!isAuthenticated) {
            return;
        }

        setShowLoginDialog(false);
        setShowSignupDialog(false);
    }, [isAuthenticated, setShowLoginDialog, setShowSignupDialog]);

    useEffect(() => {
        if (typeof window === 'undefined') {
            return;
        }

        const url = new URL(window.location.href);
        if (url.searchParams.get(AUTH_PROMPT_PARAM) !== AUTH_PROMPT_LOGIN) {
            return;
        }

        setShowLoginDialog(true);

        url.searchParams.delete(AUTH_PROMPT_PARAM);
        window.history.replaceState(
            window.history.state,
            '',
            `${url.pathname}${url.search}${url.hash}`,
        );
    }, [setShowLoginDialog]);

    useEffect(() => {
        if (isCommunityUser) {
            if (userOwnedCommunities?.length) setUserOwnedCommunities([]);
            if (selectedPrivateCommunity) setSelectedPrivateCommunity(null);
            return;
        }

        if (loadingOwnedCommunities || !isAuthenticated) {
            return;
        }

        if (!privateCommunitiesData) {
            const alreadyEmpty = (userOwnedCommunities?.length ?? 0) === 0;
            if (!alreadyEmpty) {
                setUserOwnedCommunities([]);
            }
            if (selectedPrivateCommunity) {
                setSelectedPrivateCommunity(null);
            }
            return;
        }

        const mergedOwned = mergeOwnedCommunitiesByUpdatedAt(userOwnedCommunities, privateCommunitiesData);

        // Preserve the user's current selection when data refreshes (e.g. after saving a profile),
        // but prefer the freshest version by updatedAt.
        const resolvedSelection = resolveManagedCommunitySelection({
            isCommunityAdminView,
            pathname: location.pathname,
            communities: mergedOwned,
            currentSelection: selectedPrivateCommunity,
        });

        if (!areCommunitiesEqual(userOwnedCommunities, mergedOwned)) {
            setUserOwnedCommunities(mergedOwned ?? []);
        }

        if (
            resolvedSelection &&
            (!selectedPrivateCommunity ||
                selectedPrivateCommunity.id !== resolvedSelection.id ||
                selectedPrivateCommunity.updatedAt !== resolvedSelection.updatedAt)
        ) {
            setSelectedPrivateCommunity(resolvedSelection);
        }

    }, [
        isCommunityUser,
        isAuthenticated,
        loadingOwnedCommunities,
        privateCommunitiesData,
        selectedPrivateCommunity?.id,
        userOwnedCommunities,
        isCommunityAdminView,
        location.pathname,
    ]);

    useEffect(() => {
        if (usePrivateDiscoveryData) {
            setCommunities(ownedCommunities.map(toCommunityViewPublic));
            return;
        }

        if (!shouldLoadPublicDiscovery) {
            setCommunities([]);
            return;
        }

        setCommunities(publicCommunitiesData ?? []);
    }, [usePrivateDiscoveryData, ownedCommunities, publicCommunitiesData, setCommunities, shouldLoadPublicDiscovery]);

    useEffect(() => {
        if (!shouldLoadPublicDiscovery || !publicCommunitiesError) return;
        console.error('Failed to load public communities', publicCommunitiesErrorDetails);
        toast.error('Unable to load communities', {
            description: publicCommunitiesErrorDetails instanceof Error
                ? publicCommunitiesErrorDetails.message
                : 'Please refresh the page and try again.',
        });
    }, [publicCommunitiesError, publicCommunitiesErrorDetails, shouldLoadPublicDiscovery]);


    //#region User type setup
    const handleUserTypeConfirm = async (nextType: SelectableUserType) => {
        if (!allowedUserTypes.includes(nextType)) {
            toast.error('You cannot switch to that role with your current access.');
            return;
        }

        const isOnboardingSelection = !displayUser?.userType;
        const nextTypeLabel = nextType === 'community-owner'
            ? 'Community Owner'
            : nextType === 'community-admin'
                ? 'Community Admin'
                : 'Community Member';

        setIsSavingUserType(true);
        try {
            await updateUserProfile({ userType: nextType });
            toast.success(isOnboardingSelection ? 'View selected' : 'View updated', {
                description: isOnboardingSelection
                    ? `${nextTypeLabel} is ready. You can switch views later from your profile menu.`
                    : `You are now viewing the dashboard as ${nextTypeLabel}.`,
            });
            setUserTypeDialogValue(nextType);
            setShowUserTypeDialog(false);
        } catch (error: any) {
            console.error('Failed to update user type', error);
            toast.error(error?.message || 'Sorry, something went wrong on our side, please refresh the page and try again.');
        } finally {
            setIsSavingUserType(false);
        }
    };
    //#endregion

    //#region Notification handlers
    const handleMarkNotificationAsRead = (id: string) => {
        markNotificationAsRead(id);
        markNotificationAsReadMutation.mutate(id, {
            onError: () => {
                setNotifications(_notifications);
            },
        });
    };

    const handleMarkAllNotificationsAsRead = () => {
        markAllNotificationsAsRead();
        markAllNotificationsAsReadMutation.mutate(undefined, {
            onError: () => {
                setNotifications(_notifications);
            },
        });
    };

    const handleClearAllNotifications = () => {
        clearNotifications();
        deleteAllNotificationsMutation.mutate(undefined, {
            onError: () => {
                setNotifications(_notifications);
            },
        });
    };

    const handleDeleteNotification = (id: string) => {
        const remaining = _notifications.filter((notification) => notification.id !== id);
        setNotifications(remaining);
        deleteNotificationMutation.mutate(id, {
            onError: () => {
                setNotifications(_notifications);
            },
        });
    };

    const handleRetryKycFromNotification = async () => {
        setIsRetryingKyc(true);
        try {
            const result = await InitKycSession();

            if (result.alreadyVerified || result.status === 'verified') {
                setFeedbackNotificationDialog(null);
                toast.success('Your account is already verified.');
                return;
            }

            if (result.sessionUrl) {
                window.location.assign(result.sessionUrl);
                return;
            }

            toast.error('Unable to start uniqueness verification right now. Please try again.');
        } catch (error) {
            console.error('Failed to retry KYC from notification:', error);
            toast.error('Unable to start uniqueness verification right now. Please try again.');
        } finally {
            setIsRetryingKyc(false);
        }
    };

    const handleNotificationClick = (notification: Notification) => {
        // Mark notification as read
        handleMarkNotificationAsRead(notification.id);

        const resolvedTarget = resolveNotificationNavigation(
            notification,
            campaignCommunityIndex,
        );
        if (resolvedTarget) {
            const targetCommunity = resolvedTarget.communityId
                ? communities.find((community) => community.id === resolvedTarget.communityId) ?? null
                : null;
            const targetCampaign = resolvedTarget.communityId && resolvedTarget.campaignId
                ? campaignsByCommunityId[resolvedTarget.communityId]?.campaigns.find(
                    (campaign) => campaign.id === resolvedTarget.campaignId,
                ) ?? null
                : null;

            if (targetCommunity) {
                setSelectedCommunity(targetCommunity);
            }

            if (resolvedTarget.view === 'detail') {
                setSelectedCampaign(targetCampaign);
            }

            navigateToView(resolvedTarget.view, {
                communityId: resolvedTarget.communityId,
                campaignId: resolvedTarget.campaignId,
            });
            if (mainScrollRef.current) {
                mainScrollRef.current.scrollTop = 0;
            }
            return;
        }

        // Handle different notification types with specific actions
        switch (notification.type) {
            case 'block-earned':
                // Take user to their Achievements page to see their progress
                setAchievementsViewUser(null);
                navigateToView('achievements');
                if (mainScrollRef.current) {
                    mainScrollRef.current.scrollTop = 0;
                }
                celebrate({
                    type: 'block-generated',
                    intensity: 'minimal',
                    title: '+1 Block',
                    message: 'Progress updated',
                    asset: 'bbb-logo',
                    badgeProgress: buildKnownCurrentBlockBadgeProgress(
                        user?.blockBalance,
                        notification.metadata?.blocksEarned,
                    ),
                });

                // Show celebratory toast
                setTimeout(() => {
                    toast.success('🎉 Block Earned!', {
                        description: `You've earned 1 Block! Keep going to reach the next badge tier.`,
                        duration: 4000,
                    });
                }, 500);
                break;

            case 'feedback':
                // Two scenarios:
                // 1. Quest APPROVED → User can see progress and continue with other quests
                // 2. Quest REJECTED → User needs to find and resubmit the rejected quest
                if (notification.metadata?.communityName) {
                    const community = communities.find(p => p.profile.name === notification.metadata?.communityName);
                    if (community) {
                        // For REJECTED quests, take user directly to the specific campaign to resubmit
                        if (notification.metadata.status === 'rejected' && notification.metadata.campaignName) {
                            const campaigns = campaignsByCommunityId[community.id]?.campaigns ?? [];
                            const targetCampaign = campaigns.find(c => c.name === notification?.metadata?.campaignName);

                            if (targetCampaign) {
                                // Set both community and campaign to navigate directly to the quest
                                setSelectedCommunity(community);
                                setSelectedCampaign(targetCampaign);
                                navigateToView('dashboard', { communityId: community.id });
                                if (mainScrollRef.current) {
                                    mainScrollRef.current.scrollTop = 0;
                                }

                                // Show toast with feedback to help user understand what to fix
                                setTimeout(() => {
                                    const questTitle = notification.metadata?.questTitle || 'the quest';
                                    const feedback = notification.metadata?.feedback || 'Please review the requirements and resubmit.';
                                    toast.error(`Quest Rejected: ${questTitle}`, {
                                        description: feedback,
                                        duration: 6000,
                                    });
                                }, 500);
                                return;
                            }
                        }

                        // For APPROVED quests or if campaign not found, just go to dashboard
                        setSelectedCommunity(community);
                        navigateToView('dashboard', { communityId: community.id });
                        if (mainScrollRef.current) {
                            mainScrollRef.current.scrollTop = 0;
                        }

                        // Show success message for approved quests
                        if (notification.metadata.status === 'approved') {
                            setTimeout(() => {
                                const questTitle = notification.metadata?.questTitle || 'the quest';
                                toast.success(`Quest Approved: ${questTitle}`, {
                                    description: 'Great work! Keep completing quests to earn more blocks.',
                                    duration: 4000,
                                });
                            }, 500);
                        }
                    }
                }
                break;

            case 'kyc':
                setFeedbackNotificationDialog({
                    title: notification.title,
                    feedback: notification.metadata?.feedback || notification.message,
                    type: 'kyc',
                });
                break;

            case 'badge-upgrade':
                // Take user to their Achievements page to celebrate
                setAchievementsViewUser(null);
                achievementCelebrationStorage.setPending({
                    userId: user.id,
                    sourceId: notification.id,
                    title: notification.title,
                    message: notification.message,
                });
                navigateToView('achievements');
                if (mainScrollRef.current) {
                    mainScrollRef.current.scrollTop = 0;
                }
                break;

            case 'campaign-live':
            case 'quest-deadline':
                // Take user to the community dashboard to view the campaign
                if (notification.metadata?.communityName) {
                    const community = communities.find(c => c.profile.name === notification.metadata?.communityName);
                    if (community) {
                        setSelectedCommunity(community);
                        navigateToView('dashboard', { communityId: community.id });
                        if (mainScrollRef.current) {
                            mainScrollRef.current.scrollTop = 0;
                        }
                    }
                }
                break;

            case 'campaign-ended':
                // Take user to the community leaderboard to see if they won
                if (notification.metadata?.communityName) {
                    const community = communities.find(p => p.profile.name === notification.metadata?.communityName);
                    if (community) {
                        setSelectedCommunity(community);
                        navigateToView('dashboard', { communityId: community.id }); // Dashboard has leaderboard tab
                        if (mainScrollRef.current) {
                            mainScrollRef.current.scrollTop = 0;
                        }
                    }
                }
                break;

            case 'quest-retry':
                // Take user to the campaign to retry the quest
                if (notification.metadata?.communityName) {
                    const community = communities.find(c => c.profile.name === notification.metadata?.communityName);
                    if (community) {
                        setSelectedCommunity(community);
                        navigateToView('dashboard', { communityId: community.id });
                        if (mainScrollRef.current) {
                            mainScrollRef.current.scrollTop = 0;
                        }
                    }
                }
                break;

            case 'achievement':
                // Take user to Achievements page
                setAchievementsViewUser(null);
                achievementCelebrationStorage.setPending({
                    userId: user.id,
                    sourceId: notification.id,
                    title: notification.title,
                    message: notification.message,
                });
                navigateToView('achievements');
                if (mainScrollRef.current) {
                    mainScrollRef.current.scrollTop = 0;
                }
                break;

            case 'referral-milestone':
                // Take user to Referrals page
                navigateToView('referrals');
                if (mainScrollRef.current) {
                    mainScrollRef.current.scrollTop = 0;
                }
                break;

            case 'reward':
                // Take user to Rewards Hub
                navigateToView('rewards-hub');
                if (mainScrollRef.current) {
                    mainScrollRef.current.scrollTop = 0;
                }
                break;

            default:
                // For any other notification types, just mark as read (already done above)
                break;
        }
    };
    //#endregion

    const privateCampaignsByCommunityId = useCampaignsForCommunities(
        usePrivateDiscoveryData ? ownedCommunityIds : [],
    );

    const publicCampaignsByCommunityId = useMemo(() => {
        if (!usePrivateDiscoveryData) {
            const grouped = allLiveCampaigns.reduce(
                (acc, campaign) => {
                    const list = acc[campaign.communityId] ?? [];
                    list.push(campaign);
                    acc[campaign.communityId] = list;
                    return acc;
                },
                {} as Record<string, CampaignPublicView[]>,
            );

            return communities.reduce(
                (acc, community) => {
                    const campaigns = grouped[community.id] ?? [];
                    acc[community.id] = {
                        campaigns,
                        isLoading: isLoadingAllLiveCampaigns,
                        isFetching: isFetchingAllLiveCampaigns,
                    };
                    return acc;
                },
                {} as Record<
                    string,
                    {
                        campaigns: CampaignPublicView[];
                        isLoading: boolean;
                        isFetching: boolean;
                    }
                >,
            );
        }

        return {};
    }, [
        allLiveCampaigns,
        communities,
        isFetchingAllLiveCampaigns,
        isLoadingAllLiveCampaigns,
        usePrivateDiscoveryData,
    ]);

    const campaignsByCommunityId = usePrivateDiscoveryData
        ? privateCampaignsByCommunityId
        : publicCampaignsByCommunityId;

    const campaignCommunityIndex = useMemo(
        () => buildCampaignCommunityIndex(campaignsByCommunityId),
        [campaignsByCommunityId],
    );

    // Track campaign loading state for showing proper loaders instead of empty states
    const campaignsLoading = useMemo(() => {
        if (usePrivateDiscoveryData) {
            return Object.values(privateCampaignsByCommunityId).some(
                ({ isLoading, isFetching }) => isLoading || isFetching,
            );
        }

        return (
            isLoadingAllLiveCampaigns ||
            isFetchingAllLiveCampaigns ||
            Object.values(publicCampaignsByCommunityId).some(
                ({ isLoading, isFetching }) => isLoading || isFetching,
            )
        );
    }, [
        usePrivateDiscoveryData,
        privateCampaignsByCommunityId,
        publicCampaignsByCommunityId,
        isLoadingAllLiveCampaigns,
        isFetchingAllLiveCampaigns,
    ]);

    // Check for campaigns that have ended and send raffle result notifications
    useEffect(() => {
        const checkEndedCampaigns = () => {
            const today = new Date();
            today.setHours(0, 0, 0, 0);

            // Check all projects for ended campaigns
            communities.forEach(community => {
                const campaigns = campaignsByCommunityId[community.id]?.campaigns ?? [];

                campaigns.forEach(campaign => {
                    // Check if campaign should end today
                    if (campaign.endDate && getLatestCampaignStatus(campaign) === 'live') {
                        const endDate = new Date(campaign.endDate);
                        endDate.setHours(23, 59, 59, 999);

                        // Campaign ended today
                        if (endDate < today) {
                            const timestamp = nowEpochMs();

                            // Extract reward config
                            const rewardConfig = campaign.metadata?.rewardConfig;
                            const numberOfGuaranteedWinners = parseInt(rewardConfig?.numberOfGuaranteedWinners || '0');
                            const totalAmount = parseFloat(rewardConfig?.totalTokenAmount || '0');
                            const tokenType = rewardConfig?.tokenType || 'tokens';

                            if (numberOfGuaranteedWinners > 0 && totalAmount > 0) {
                                // Calculate raffle winners (20% of top winners)
                                const raffleWinners = Math.floor(numberOfGuaranteedWinners * 0.2);
                                const totalWinners = numberOfGuaranteedWinners + raffleWinners;
                                const prizePerWinner = (totalAmount / totalWinners).toFixed(2);

                                const existing = _notifications.some((notification) => notification.type === "campaign-ended" && notification.metadata?.campaignId === campaign.id);
                                if (existing) {
                                    return;
                                }

                                createNotificationMutation.mutate({
                                    type: "campaign-ended",
                                    title: `🏆 Campaign Ended: ${campaign.name}`,
                                    message: `The "${campaign.name}" campaign by ${community.profile.name} has concluded! ${totalWinners} winners will receive ${prizePerWinner} ${tokenType} each (${numberOfGuaranteedWinners} top leaderboard + ${raffleWinners} raffle winners). Check the leaderboard to see if you won!`,
                                    timestamp,
                                    actionUrl: `/campaigns/${campaign.id}/leaderboard`,
                                    metadata: {
                                        communityName: community.profile.name,
                                        campaignName: campaign.name,
                                        campaignId: campaign.id,
                                        prizeAmount: `${prizePerWinner} ${tokenType}`,
                                    },
                                });
                            }
                        }
                    }
                });
            });
        };

        // Check immediately on mount
        checkEndedCampaigns();

        // Check every hour
        const interval = setInterval(checkEndedCampaigns, 60 * 60 * 1000);
        return () => clearInterval(interval);
    }, [campaignsByCommunityId, communities, _notifications, createNotificationMutation]);

    // Redirect guests from restricted tabs
    useEffect(() => {
        if (!isAuthenticated) {
            if (isGuestRestrictedCampaignTab(activeTab)) {
                setActiveTab('all');
            }
        }
    }, [isAuthenticated, activeTab]);

    const communitiesLoaded = usePrivateDiscoveryData
        ? !loadingOwnedCommunities
        : !isPublicCommunitiesLoading;

    const { navigateToView } = useDashboardRouting({
        viewMode,
        setViewMode,
        selectedCommunity,
        selectedCampaign,
        setSelectedCommunity,
        setSelectedCampaign,
        communities,
        isAuthenticated,
        displayUser,
        setShowLoginDialog,
        setPublicProfileUserId,
        isAuthReady,
        communitiesLoaded,
        isUserProfileLoading,
        preferPrivateCampaigns: usePrivateDiscoveryData,
        adminCommunityIds: ownedCommunityIds,
        adminCommunitiesLoaded: !loadingOwnedCommunities,
    });

    useEffect(() => {
        if (!isCommunityAdminView || loadingOwnedCommunities || !isAuthenticated) {
            return;
        }

        const ownedCount = privateCommunitiesData?.length ?? 0;
        if (ownedCount !== 0) {
            return;
        }

        // if no community owned then clean up ui:

        const hasOwnedCommunities = (userOwnedCommunities?.length ?? 0) > 0;
        const hasSelection = Boolean(selectedPrivateCommunity);

        // Avoid dispatching identical state updates every render (which caused the depth loop).
        if (hasOwnedCommunities) {
            setUserOwnedCommunities([]);
        }

        if (hasSelection) {
            setSelectedPrivateCommunity(null);
        }

        if (viewMode !== 'community-admin') {
            navigateToView('create-community', { replace: true });
        }
    }, [
        isCommunityAdminView,
        isAuthenticated,
        loadingOwnedCommunities,
        privateCommunitiesData,
        userOwnedCommunities?.length,
        selectedPrivateCommunity?.id,
        viewMode,
        navigateToView,
        setUserOwnedCommunities,
        setSelectedPrivateCommunity,
    ]);


    // Hero carousel should only include communities that currently have live campaigns.
    const promotedProjects = useMemo(
        () =>
            communities.filter((community) => {
                const communityCampaigns = campaignsByCommunityId[community.id]?.campaigns ?? [];
                return hasLiveCampaignsHelper(communityCampaigns);
            }),
        [communities, campaignsByCommunityId],
    );

    const handleCommunityClick = (community: CommunityViewPublic, campaign?: any) => {
        setSelectedCommunity(community);

        const campaigns = campaignsByCommunityId[community.id]?.campaigns ?? [];
        if (campaign) {
            setSelectedCampaign(campaign);
            navigateToView('detail', { communityId: community.id, campaignId: campaign.id });
            return;
        }

        const liveCampaigns = campaigns.filter(c =>
            getLatestCampaignStatus(c) === 'live'
        );
        setSelectedCampaign(liveCampaigns.length > 0 ? liveCampaigns[0] : campaigns[0] ?? null);
        navigateToView('dashboard', { communityId: community.id });
    };

    // Handle favorite changes to trigger count recalculation
    const handleFavoriteChange = () => {
        setFavoritesTrigger(prev => prev + 1);
    };

    // Handle campaign updates to trigger immediate refresh
    const handleCampaignUpdate = () => {
        console.log('🔄 Campaign updated - refreshing campaign list...');
        setCampaignUpdateTrigger(prev => prev + 1);
        queryClient.invalidateQueries({ queryKey: ['campaigns'] });
    };

    // Ensure authenticated users are routed to the full dashboard view when needed
    useEffect(() => {
        if (!selectedCommunity) return;

        if (isAuthenticated && displayUser && viewMode === 'detail') {
            campaignProgressStorage.startCampaign(displayUser.id, selectedCommunity.id);
        }
    }, [displayUser, isAuthenticated, navigateToView, selectedCommunity, viewMode]);

    const handleBackToList = () => {
        navigateToView('list', { replace: true });
        setSelectedCommunity(null);
        setSelectedCampaign(null);
        setAchievementsViewUser(null);
    };

    const handleBackToCommunity = () => {
        if (selectedCommunity) {
            navigateToView('dashboard', { communityId: selectedCommunity.id, replace: true });
            return;
        }
        handleBackToList();
    };

    const requireAuthenticatedNavigation = (action: () => void) => {
        if (!displayUser || !isAuthenticated) {
            setShowLoginDialog(true);
            return false;
        }
        action();
        return true;
    };

    const handleReferralsClick = () => {
        requireAuthenticatedNavigation(() => navigateToView('referrals'));
    };

    const openPolicyInNewTab = (view: 'privacy-policy' | 'terms-of-service') => {
        if (typeof window === 'undefined') {
            return;
        }

        const url = `${window.location.origin}${buildPathForView(view)}`;
        window.open(url, '_blank', 'noopener,noreferrer');
    };

    const handleRewardsHubClick = () => {
        requireAuthenticatedNavigation(() => navigateToView('rewards-hub'));
    };

    const handleProfileSettingsClick = () => {
        requireAuthenticatedNavigation(() => navigateToView('profile-settings'));
    };

    const handleActiveQuestsClick = () => {
        requireAuthenticatedNavigation(() => navigateToView('active-quests'));
    };

    const resolveSelectableUserType = (): SelectableUserType | null => {
        if (displayUser?.userType === 'community-owner') return 'community-owner';
        if (displayUser?.userType === 'community-user') return 'community-user';
        if (displayUser?.userType === 'community-admin') return 'community-admin';
        return null;
    };

    const handleChangeUserTypeClick = () => {
        requireAuthenticatedNavigation(() => {
            const resolved = resolveSelectableUserType();
            setUserTypeDialogValue(resolved && allowedUserTypes.includes(resolved) ? resolved : allowedUserTypes[0]);
            setShowUserTypeDialog(true);
        });
    };

    const handleTabChange = (tab: TabFilter) => {
        // Guest users cannot access these tabs
        if (!isAuthenticated && isGuestRestrictedCampaignTab(tab)) {
            // Redirect guests to 'all' tab if they try to access restricted tabs
            setActiveTab('all');
            return;
        }

        // Save current scroll position
        if (mainScrollRef.current) {
            scrollPositionRef.current = mainScrollRef.current.scrollTop;
        }

        // Reset badge tier when switching away from badges tab
        if (tab !== 'badges' && activeTab === 'badges') {
            setSelectedBadgeTier('all');
        }

        // Also reset when switching TO badges tab
        if (tab === 'badges') {
            setSelectedBadgeTier('all');
        }

        setActiveTab(tab);

        // Restore scroll position after a brief delay to allow re-render
        setTimeout(() => {
            if (mainScrollRef.current) {
                mainScrollRef.current.scrollTop = scrollPositionRef.current;
            }
        }, 0);
    };

    const handleSelectManagedCommunity = (community: CommunityRecord) => {
        upsertPrivateCommunity(community);
        setSelectedPrivateCommunity(community);
        navigateToView('community-admin', { communityId: community.id });
        setShowCommunityPicker(false);
    };

    const handleCreateCommunityClick = () => {
        if (!isAuthenticated) {
            setShowCreateCommunityAuthDialog(true);
            return;
        }

        const managedCount = userOwnedCommunities?.length ?? userOwnedCommunities?.length;
        const hasOwnedCommunities = (userOwnedCommunities?.length ?? 0) > 0;

        if (managedCount > 1 && userOwnedCommunities?.length > 0) {
            setShowCommunityPicker(true);
            return;
        }

        if (managedCount === 1 && userOwnedCommunities?.length === 1) {
            handleSelectManagedCommunity(userOwnedCommunities[0]);
            return;
        }

        // Use the state that's already computed to avoid race conditions
        // If newProjectData exists, the user has already created a community
        navigateToView(hasOwnedCommunities ? 'community-admin' : 'create-community');
    };

    // Calculate tab counts (shown in filter labels when count > 0)
    const tabCounts = useMemo(() => {
        const counts: Partial<Record<TabFilter, number>> = {
            all: 0,
            spotlight: 0,
            active: 0,
            badges: 0,
            favourites: 0,
            ended: 0,
        };

        communities.forEach((community) => {
            const campaigns = campaignsByCommunityId[community.id]?.campaigns ?? [];

            campaigns.forEach((campaign) => {
                const status = getLatestCampaignStatus(campaign);
                const isLive = status === 'live';
                const isEnded = status === 'ended';
                const isVisibleInDiscovery = isLive || isEnded;

                if (!isVisibleInDiscovery) return;

                counts.all = (counts.all ?? 0) + 1;
                counts.spotlight = (counts.spotlight ?? 0) + 1;

                if (isLive) counts.active = (counts.active ?? 0) + 1;
                if (isEnded) counts.ended = (counts.ended ?? 0) + 1;

                if (campaign.metadata?.userRestriction) {
                    counts.badges = (counts.badges ?? 0) + 1;
                }

                if (displayUser && isAuthenticated) {
                    const campaignFavoriteId = `${community.id}-${campaign.id}`;
                    if (favoritesStorage.isFavorited(displayUser.id, campaignFavoriteId)) {
                        counts.favourites = (counts.favourites ?? 0) + 1;
                    }
                }
            });
        });

        return counts;
    }, [communities, campaignsByCommunityId, displayUser?.id, isAuthenticated, favoritesTrigger, campaignUpdateTrigger]);

    // Filter projects based on active tab and search query
    // Note: For 'favourites' tab, we'll handle filtering at the campaign level in campaignCards
    const filteredProjects = useMemo(() => {
        const filtered = communities.filter((community) => {
            const communityCampaigns = campaignsByCommunityId[community.id]?.campaigns ?? [];

            // Check if community has live campaigns using centralized logic
            const hasLiveCampaignsForCommunity = () => {
                return hasLiveCampaignsHelper(communityCampaigns);
            };

            // Check if community has ended campaigns using centralized logic
            const hasEndedCampaignsForCommunity = () => {
                return hasEndedCampaignsHelper(communityCampaigns);
            };

            const matchesTab =
                activeTab === 'all' ||
                (activeTab === 'active' && (hasLiveCampaignsForCommunity() || community.profile.status === 'active')) ||
                (activeTab === 'spotlight') || //! TODO add community.isSpotlight
                (activeTab === 'ended' && hasEndedCampaignsForCommunity()) ||
                (activeTab === 'favourites') || // Show all projects for favourites tab - we'll filter campaigns below
                (activeTab === 'badges' && (hasLiveCampaignsForCommunity() || community.profile.status === 'active'));

            return matchesTab;
        });

        return filtered;
    }, [communities, activeTab, displayUser?.id, isAuthenticated, favoritesTrigger, campaignUpdateTrigger, campaignsByCommunityId]);

    // Generate campaign cards: one card per campaign (not per community)
    const campaignCards = useMemo(() => {
        const cards: Array<{ community: CommunityViewPublic; campaign: any }> = [];

        // If badges tab is active but no specific tier is selected, show nothing
        if (activeTab === 'badges' && selectedBadgeTier === 'all') {
            return [];
        }

        filteredProjects.forEach((community) => {
            const campaigns = campaignsByCommunityId[community.id]?.campaigns ?? [];

            // Filter campaign cards by current tab status rules.
            const statusFilteredCampaigns = campaigns.filter((campaign) => {
                const status = getLatestCampaignStatus(campaign);

                if (activeTab === 'active') return status === 'live';
                if (activeTab === 'ended') return status === 'ended';

                // For all other public list tabs, keep previously visible campaign statuses.
                return status === 'live' || status === 'ended';
            });

            if (statusFilteredCampaigns.length > 0) {
                statusFilteredCampaigns.forEach(campaign => {
                    if (campaign && campaign.id) {
                        // Apply badge tier filtering at campaign level
                        const campaignBadgeRequirement = campaign.metadata?.userRestriction;
                        const matchesBadgeTier = selectedBadgeTier === 'all' ||
                            (campaignBadgeRequirement && campaignBadgeRequirement.toLowerCase() === selectedBadgeTier.toLowerCase());
                        const matchesSearch = matchesCampaignSearchQuery(searchQuery, community, campaign);

                        if (matchesBadgeTier && matchesSearch) {
                            cards.push({ community, campaign });
                        }
                    }
                });
            }
            // If no status-matching campaigns, don't show anything for this community.
        });

        // For favourites tab, filter to only show favorited campaigns
        if (activeTab === 'favourites' && displayUser && isAuthenticated) {
            return cards.filter(({ community, campaign }) => {
                const campaignFavoriteId = `${community.id}-${campaign.id}`;
                const isFavorited = favoritesStorage.isFavorited(displayUser.id, campaignFavoriteId);

                // Also apply badge tier filtering
                if (selectedBadgeTier !== 'all') {
                    const campaignBadgeRequirement = campaign.metadata?.userRestriction;
                    const matchesBadgeTier = campaignBadgeRequirement &&
                        campaignBadgeRequirement.toLowerCase() === selectedBadgeTier.toLowerCase();
                    return isFavorited && matchesBadgeTier;
                }

                return isFavorited;
            });
        }

        return cards;
    }, [filteredProjects, campaignUpdateTrigger, activeTab, displayUser?.id, isAuthenticated, favoritesTrigger, selectedBadgeTier, searchQuery, campaignsByCommunityId]);

    const showAdminDashboard = Boolean(viewMode === 'community-admin' && userOwnedCommunities && displayUser && selectedPrivateCommunity);

    return (
        <>
            <Toaster
                expand={false}
                richColors
            />
            {shouldShowLoading ? (
                <PageLoader />
            ) : (
                <SidebarProvider defaultOpen={showAdminDashboard && !isMobileLayout}>
                    <Suspense fallback={<PageLoader />}>
                        <CommunityUserDashboard
                            navigateToView={navigateToView}
                            handleCommunityClick={handleCommunityClick}
                            handleCreateCommunityClick={handleCreateCommunityClick}
                            handleProfileSettingsClick={handleProfileSettingsClick}
                            handleActiveQuestsClick={handleActiveQuestsClick}
                            handleReferralsClick={handleReferralsClick}
                            handleRewardsHubClick={handleRewardsHubClick}
                            handleChangeUserType={handleChangeUserTypeClick}
                            handleMarkNotificationAsRead={handleMarkNotificationAsRead}
                            handleMarkAllNotificationsAsRead={handleMarkAllNotificationsAsRead}
                            handleClearAllNotifications={handleClearAllNotifications}
                            handleDeleteNotification={handleDeleteNotification}
                            handleNotificationClick={handleNotificationClick}
                            mainScrollRef={mainScrollRef}
                            setAchievementsViewUser={setAchievementsViewUser}
                            handleBackToList={handleBackToList}
                            promotedProjects={promotedProjects}
                            handleTabChange={handleTabChange}
                            tabCounts={tabCounts}
                            campaignCards={campaignCards}
                            campaignsByCommunityId={campaignsByCommunityId}
                            handleFavoriteChange={handleFavoriteChange}
                            communities={communities}
                            notifications={_notifications}
                            achievementsViewUser={achievementsViewUser}
                            publicProfileUserId={publicProfileUserId}
                            communitiesLoaded={communitiesLoaded}
                            campaignsLoading={campaignsLoading}
                            handleBackToCommunity={handleBackToCommunity}
                            showAdminDashboard={showAdminDashboard}
                            onAdminBack={handleBackToList}
                            onAdminCampaignUpdated={handleCampaignUpdate}
                        />
                    </Suspense>

                    {/* Auth Dialogs */}
                    <Suspense fallback={null}>
                        <LoginDialog
                            open={showLoginDialog}
                            onOpenChange={setShowLoginDialog}
                            onSwitchToSignup={() => {
                                setShowLoginDialog(false);
                                setShowSignupDialog(true);
                            }}
                        />
                        <SignupDialog
                            open={showSignupDialog}
                            onOpenChange={setShowSignupDialog}
                            onSwitchToLogin={() => {
                                setShowSignupDialog(false);
                                setShowLoginDialog(true);
                            }}
                            onPrivacyPolicyClick={() => openPolicyInNewTab('privacy-policy')}
                            onTermsOfServiceClick={() => openPolicyInNewTab('terms-of-service')}
                        />
                    </Suspense>

                    <UserTypeDialog
                        open={showUserTypeDialog}
                        value={userTypeDialogValue}
                        allowedTypes={allowedUserTypes}
                        mode={displayUser?.userType ? 'switch' : 'onboarding'}
                        onOpenChange={displayUser?.userType ? setShowUserTypeDialog : undefined}
                        onConfirm={handleUserTypeConfirm}
                        isSaving={isSavingUserType}
                    />

                    {/* Create Project Authentication Required Dialog */}
                    <AlertDialog open={showCreateCommunityAuthDialog} onOpenChange={setShowCreateCommunityAuthDialog}>
                        <AlertDialogContent>
                            <AlertDialogHeader>
                                <AlertDialogTitle>Create an Account to Continue</AlertDialogTitle>
                                <AlertDialogDescription>
                                    To create and manage your own community community, you need to have an account. Create one now to get started!
                                </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                                <AlertDialogCancel>Cancel</AlertDialogCancel>
                                <AlertDialogAction
                                    onClick={() => {
                                        setShowCreateCommunityAuthDialog(false);
                                        setShowLoginDialog(true);
                                    }}
                                >
                                    Log In
                                </AlertDialogAction>
                                <AlertDialogAction
                                    onClick={() => {
                                        setShowCreateCommunityAuthDialog(false);
                                        setShowSignupDialog(true);
                                    }}
                                    className="bg-primary hover:bg-primary/90"
                                >
                                    Create Account
                                </AlertDialogAction>
                            </AlertDialogFooter>
                        </AlertDialogContent>
                    </AlertDialog>

                    <Dialog open={showCommunityPicker} onOpenChange={setShowCommunityPicker}>
                        <DialogContent>
                            <motion.div {...dashboardMotion.revealStaticCompactItemProps(0)}>
                                <DialogHeader>
                                    <DialogTitle>Choose the community</DialogTitle>
                                    <DialogDescription>
                                        you want to manage now
                                    </DialogDescription>
                                </DialogHeader>
                            </motion.div>
                            <motion.div
                                className="max-h-[60vh] space-y-2 overflow-y-auto py-2"
                                {...dashboardMotion.revealListProps}
                            >
                                {user && userOwnedCommunities?.map((community, index) => {
                                    const isOwner = user.id === community.ownerId;
                                    const isCurrent = selectedPrivateCommunity?.id === community.id;
                                    const isVerified = Boolean(
                                        community.profile.verifiedSocials?.twitter?.verifiedAt ||
                                        community.profile.verifiedSocials?.discord?.verifiedAt
                                    );
                                    return (
                                        <motion.div
                                            key={community.id}
                                            {...dashboardMotion.revealStaticCompactItemProps(index + 1)}
                                        >
                                            <Button
                                                variant="outline"
                                                aria-current={isCurrent ? 'page' : undefined}
                                                className={cn(
                                                    "w-full justify-between py-10 text-left",
                                                    isCurrent && "border-primary/60 bg-primary/5 shadow-[inset_3px_0_0_var(--primary)]",
                                                )}
                                                onClick={() => handleSelectManagedCommunity(community)}
                                            >
                                                <div className="flex min-w-0 items-center gap-3">
                                                    <img
                                                        src={community.profile.logo.url!}
                                                        alt={community.profile.name}
                                                        className="size-10 shrink-0 rounded-md object-cover"
                                                    />
                                                    <div className="min-w-0 text-left">
                                                        <div className="flex items-center gap-1.5">
                                                            <p className="truncate font-semibold leading-tight">{community.profile.name}</p>
                                                            {isVerified && (
                                                                <img
                                                                    src={verifiedBadge}
                                                                    alt="Verified"
                                                                    className="size-3.5 shrink-0"
                                                                    title="Verified community"
                                                                />
                                                            )}
                                                        </div>
                                                        <div className="mt-1">
                                                            <Badge
                                                                variant={isOwner ? 'default' : 'secondary'}
                                                                className="text-[10px] px-2 py-0.5"
                                                            >
                                                                {isOwner ? 'Owner' : 'Admin'}
                                                            </Badge>
                                                        </div>
                                                    </div>
                                                </div>
                                                {isCurrent && (
                                                    <Badge
                                                        variant="outline"
                                                        className="ml-3 shrink-0 gap-1 border-primary/40 bg-primary/10 text-[10px] text-primary"
                                                    >
                                                        <Check className="size-3" />
                                                        Current
                                                    </Badge>
                                                )}
                                            </Button>
                                        </motion.div>
                                    );
                                })}
                                {userOwnedCommunities?.length === 0 && (
                                    <motion.p
                                        className="text-xs text-muted-foreground"
                                        {...dashboardMotion.revealStaticCompactItemProps(1)}
                                    >
                                        Nothing available yet. Refresh or create a new community.
                                    </motion.p>
                                )}
                                <motion.div {...dashboardMotion.revealStaticCompactItemProps((userOwnedCommunities?.length ?? 0) + 1)}>
                                    <Button
                                        asChild
                                        variant="outline"
                                        className="w-full justify-start py-10"
                                    >
                                        <Link
                                            to="/communities/create"
                                            onClick={(event) => {
                                                event.preventDefault();
                                                setShowCommunityPicker(false);
                                                navigateToView('create-community');
                                            }}
                                        >
                                            <div className="flex items-center gap-3">
                                                <div className="flex size-10 items-center justify-center rounded-md border border-dashed text-muted-foreground">
                                                    <Plus className="size-4" />
                                                </div>
                                                <div className="text-left">
                                                    <p className="font-semibold leading-tight">Create a new community</p>
                                                    <p className="mt-1 text-xs text-muted-foreground">Start fresh with a new space</p>
                                                </div>
                                            </div>
                                        </Link>
                                    </Button>
                                </motion.div>
                            </motion.div>
                            <motion.div {...dashboardMotion.revealStaticCompactItemProps((userOwnedCommunities?.length ?? 0) + 2)}>
                                <DialogFooter>
                                    <Button variant="outline" onClick={() => setShowCommunityPicker(false)}>
                                        Cancel
                                    </Button>
                                </DialogFooter>
                            </motion.div>
                        </DialogContent>
                    </Dialog>

                    <Dialog
                        open={Boolean(feedbackNotificationDialog)}
                        onOpenChange={(open) => {
                            if (!open) {
                                setFeedbackNotificationDialog(null);
                            }
                        }}
                    >
                        <DialogContent>
                            <DialogHeader>
                                <DialogTitle>{feedbackNotificationDialog?.title ?? 'Feedback'}</DialogTitle>
                                <DialogDescription>
                                    {feedbackNotificationDialog?.type === 'kyc'
                                        ? 'Review the rejection reason and start a new verification attempt when ready.'
                                        : 'Review the feedback attached to this notification.'}
                                </DialogDescription>
                            </DialogHeader>
                            <div className="whitespace-pre-wrap text-sm text-foreground">
                                {feedbackNotificationDialog?.feedback ?? 'No feedback provided.'}
                            </div>
                            <DialogFooter>
                                {feedbackNotificationDialog?.type === 'kyc' && (
                                    <Button
                                        onClick={handleRetryKycFromNotification}
                                        disabled={isRetryingKyc}
                                    >
                                        {isRetryingKyc && <Loader2 className="mr-2 size-4 animate-spin" />}
                                        Try again
                                    </Button>
                                )}
                                <Button
                                    variant="outline"
                                    onClick={() => setFeedbackNotificationDialog(null)}
                                    disabled={isRetryingKyc}
                                >
                                    Close
                                </Button>
                            </DialogFooter>
                        </DialogContent>
                    </Dialog>

                    <NotificationDetailsDialog
                        open={Boolean(notificationDetailsDialog)}
                        onOpenChange={(open) => {
                            if (!open) {
                                setNotificationDetailsDialog(null);
                            }
                        }}
                        title={notificationDetailsDialog?.title ?? 'Notifications'}
                        notifications={notificationDetailsDialog?.notifications ?? []}
                        onNotificationClick={handleNotificationClick}
                    />

                    {/* Feedback Button */}
                    <FeedbackButton
                        isOpen={showFeedback}
                        onOpenChange={setShowFeedback}
                        onLoginClick={() => setShowLoginDialog(true)}
                        onSignupClick={() => setShowSignupDialog(true)}
                    />

                    <CookieDisclosure />
                </SidebarProvider>
            )}
        </>
    );
}
